export type AgentActionParameterType = "string" | "number" | "boolean" | "object" | "array";

export type AgentActionParameterDefinition = {
  name: string;
  type: AgentActionParameterType;
  required?: boolean;
  description: string;
};

export type AgentActionDefinition = {
  id: string;
  name: string;
  description: string;
  requiresStaff?: boolean;
  parameters: AgentActionParameterDefinition[];
};

export type AgentPermission = {
  actionId: string;
  allowed: boolean;
  reason?: string;
  parameterConstraints?: Record<string, unknown>;
};

export type AgentQueryActionDefinition = {
  id: string;
  name: string;
  description: string;
  parameters: AgentActionParameterDefinition[];
};

export type AgentMacroContext = {
  surface: string;
  actorKind: "staff" | "consumer";
  actorRole?: string;
  currentAppId: string;
  currentAppLabel: string;
  authenticated: boolean;
  locationConfirmed?: boolean;
  platform: string;
  runtimeFamily: string;
  language?: {
    active: string;
    fallback?: string;
    supported?: string[];
  };
  i18n?: {
    runtimeProvidesAgentCopy?: boolean;
    labelsQueryActionId?: string;
  };
};

export type AgentRuntimeKind = "staff" | "consumer" | "hybrid";
export type AgentRuntimeAssistantMode = "embedded_assistant" | "native_orchestrated" | "none";

export type AgentRuntimeDefinition = {
  version: "1";
  runtime: {
    id: string;
    label: string;
    family: string;
    kind: AgentRuntimeKind;
  };
  agent: {
    available: boolean;
    language: string;
    assistantMode: AgentRuntimeAssistantMode;
    availabilityReason?: string;
  };
  ui: {
    canOpenAssistant: boolean;
    canCloseAssistant: boolean;
  };
  capabilities: {
    supportsExecuteAction: boolean;
    supportsOpenAssistant: boolean;
    supportsCloseAssistant: boolean;
  };
  context: {
    currentAppId: string;
    currentAppLabel: string;
  };
  macroContext: AgentMacroContext;
  contextBlocks?: Array<{
    id: string;
    title: string;
    data: unknown;
    updatedAt: string;
    optional?: boolean;
  }>;
  actions: AgentActionDefinition[];
  queryActions: AgentQueryActionDefinition[];
  permissions: AgentPermission[];
};

type AgentRuntimeBridgeHost = Window & typeof globalThis & {
  VolqanRuntimeAgentAPI?: {
    getVersion?: () => string;
    getDefinition?: () => string;
    getDefinitionObject?: () => AgentRuntimeDefinition;
    executeAction?: (actionId: string, rawParametersJson?: string) => string;
    executeQuery?: (queryActionId: string, rawParametersJson?: string) => string;
    openAssistant?: () => string;
    closeAssistant?: () => string;
  };
  VolqanNativeAPI?: Record<string, unknown> | null;
  Android?: Record<string, unknown> | null;
};

const text = (value: unknown): string => String(value || "").trim();

const buildDefinitionJson = (definition: AgentRuntimeDefinition): string =>
  JSON.stringify(definition);

export const installRuntimeAgentApi = (params: {
  getDefinition?: () => AgentRuntimeDefinition;
  runtimeId: string;
  runtimeLabel: string;
  runtimeFamily?: string;
  runtimeKind?: AgentRuntimeKind;
  appId: string;
  appLabel: string;
  language: string;
  actions: AgentActionDefinition[];
  permissions: AgentPermission[];
  macroContext: AgentMacroContext;
  contextBlocks?: AgentRuntimeDefinition["contextBlocks"];
  queryActions?: AgentQueryActionDefinition[];
  availabilityReason?: string;
  assistantMode?: AgentRuntimeAssistantMode;
  onOpenAssistant?: () => void;
  onCloseAssistant?: () => void;
  onExecuteAction?: (actionId: string, parameters: Record<string, unknown>) => string;
  onExecuteQuery?: (queryActionId: string, parameters: Record<string, unknown>) => string;
}): (() => void) => {
  if (typeof window === "undefined") return () => {};

  const host = window as AgentRuntimeBridgeHost;
  const normalizedActions = Array.isArray(params.actions) ? params.actions : [];
  const normalizedPermissions = Array.isArray(params.permissions) ? params.permissions : [];
  const normalizedQueryActions = Array.isArray(params.queryActions) ? params.queryActions : [];
  const canExecuteAction = typeof params.onExecuteAction === "function";
  const canExecuteQuery = typeof params.onExecuteQuery === "function";
  const canOpenAssistant = typeof params.onOpenAssistant === "function";
  const canCloseAssistant = typeof params.onCloseAssistant === "function";

  const definition: AgentRuntimeDefinition = {
    version: "1",
    runtime: {
      id: text(params.runtimeId) || "volqan-consumer-app",
      label: text(params.runtimeLabel) || "Volqan Consumer App",
      family: text(params.runtimeFamily) || "consumer",
      kind: params.runtimeKind || "consumer",
    },
    agent: {
      available: normalizedActions.length > 0 && (canOpenAssistant || canExecuteAction),
      language: text(params.language) || "en",
      assistantMode: params.assistantMode || (canOpenAssistant ? "embedded_assistant" : "none"),
      availabilityReason: text(params.availabilityReason) || undefined,
    },
    ui: {
      canOpenAssistant,
      canCloseAssistant,
    },
    capabilities: {
      supportsExecuteAction: canExecuteAction,
      supportsOpenAssistant: canOpenAssistant,
      supportsCloseAssistant: canCloseAssistant,
    },
    context: {
      currentAppId: text(params.appId) || "idle",
      currentAppLabel: text(params.appLabel) || text(params.appId) || "Consumer Runtime",
    },
    macroContext: params.macroContext,
    contextBlocks: Array.isArray(params.contextBlocks) ? params.contextBlocks : [],
    actions: normalizedActions,
    queryActions: normalizedQueryActions,
    permissions: normalizedPermissions,
  };
  const resolveDefinition = (): AgentRuntimeDefinition => {
    if (typeof params.getDefinition === "function") {
      return params.getDefinition();
    }
    return definition;
  };

  host.VolqanRuntimeAgentAPI = {
    getVersion: () => resolveDefinition().version,
    getDefinition: () => buildDefinitionJson(resolveDefinition()),
    getDefinitionObject: () => resolveDefinition(),
    executeAction: (actionId: string, rawParametersJson?: string) => {
      if (typeof params.onExecuteAction !== "function") {
        return "UNSUPPORTED_ACTION";
      }
      const normalizedActionId = text(actionId);
      if (!normalizedActionId) {
        return "ERROR:ACTION_ID_REQUIRED";
      }
      const parameters = (() => {
        const raw = text(rawParametersJson);
        if (!raw) return {};
        try {
          const parsed = JSON.parse(raw) as Record<string, unknown>;
          return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
        } catch {
          return {};
        }
      })();
      return params.onExecuteAction(normalizedActionId, parameters);
    },
    executeQuery: (queryActionId: string, rawParametersJson?: string) => {
      if (typeof params.onExecuteQuery !== "function") {
        return "UNSUPPORTED_QUERY";
      }
      const normalizedQueryActionId = text(queryActionId);
      if (!normalizedQueryActionId) {
        return "ERROR:QUERY_ID_REQUIRED";
      }
      const parameters = (() => {
        const raw = text(rawParametersJson);
        if (!raw) return {};
        try {
          const parsed = JSON.parse(raw) as Record<string, unknown>;
          return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
        } catch {
          return {};
        }
      })();
      return params.onExecuteQuery(normalizedQueryActionId, parameters);
    },
    openAssistant: () => {
      const currentDefinition = resolveDefinition();
      if (!currentDefinition.agent.available || typeof params.onOpenAssistant !== "function") {
        return "UNAVAILABLE";
      }
      params.onOpenAssistant();
      return "OK";
    },
    closeAssistant: () => {
      if (typeof params.onCloseAssistant !== "function") {
        return "UNAVAILABLE";
      }
      params.onCloseAssistant();
      return "OK";
    },
  };

  return () => {
    delete host.VolqanRuntimeAgentAPI;
  };
};
